import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle } from "lucide-react";

const AuthCallback = () => {
  const { setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [authError, setAuthError] = useState(null);
  const [processing, setProcessing] = useState(true);

  const processAuth = async () => {
    setProcessing(true);
    setAuthError(null);

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (!sessionIdMatch) {
      const errMsg = "No session_id found in redirect URL. The authentication redirect may have failed.";
      setAuthError(errMsg);
      setProcessing(false);
      toast.error(errMsg);
      return;
    }

    const sessionId = sessionIdMatch[1];

    // Retry up to 3 times — iOS can have transient network issues during redirect
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        // First try with credentials (works when same-origin or CORS properly configured)
        // If that fails with Network Error, retry without credentials + use token fallback
        const useCredentials = attempt === 1;
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: useCredentials, timeout: 15000 }
        );

        const userData = response.data;

        // Store token in sessionStorage as fallback for subsequent requests
        // This handles iOS Safari ITP and infrastructure CORS issues
        if (response.headers?.["x-session-token"]) {
          sessionStorage.setItem("_st_fb", btoa(response.headers["x-session-token"]));
        } else if (response.data?.session_token) {
          sessionStorage.setItem("_st_fb", btoa(response.data.session_token));
        }

        setUser(userData);
        setProcessing(false);
        toast.success(`Welcome, ${userData.name}!`);

        // Honour post-login redirect intent (e.g., /join/:token)
        let target;
        try {
          const pending = sessionStorage.getItem("post_login_redirect");
          if (pending) {
            sessionStorage.removeItem("post_login_redirect");
            target = pending;
          }
        } catch { /* ignore */ }
        if (!target) {
          target = userData.role === "parent" ? "/parent"
            : (userData.role === "school_admin" || userData.role === "teacher") ? "/school"
            : "/dashboard";
        }
        navigate(target, { replace: true, state: { user: userData } });
        return; // success
      } catch (error) {
        lastError = error;
        // If Network Error on first attempt (CORS blocking credentials),
        // next attempt will try without credentials
        if (attempt < 3) {
          await new Promise(r => setTimeout(r, attempt * 500));
        }
      }
    }

    // All retries failed — show detailed error on screen
    const status = lastError?.response?.status || "no response";
    const detail = lastError?.response?.data?.detail
      || lastError?.message
      || "Unknown error";
    const errMsg = `Auth failed (${status}): ${detail}. API: ${API}/auth/session`;
    setAuthError(errMsg);
    setProcessing(false);
    toast.error(`Authentication failed: ${detail}`);
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-red-100 shadow-sm p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Authentication Failed</h2>
          <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3 break-all font-mono" data-testid="auth-error-detail">{authError}</p>
          <div className="flex gap-3">
            <Button
              onClick={() => { hasProcessed.current = false; processAuth(); }}
              className="flex-1 bg-violet-600 hover:bg-violet-700 rounded-xl gap-2"
              data-testid="auth-retry-btn"
            >
              <RefreshCw className="w-4 h-4" /> Try Again
            </Button>
            <Button
              onClick={() => navigate("/", { replace: true })}
              variant="outline"
              className="flex-1 rounded-xl"
              data-testid="auth-back-btn"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
        <p className="text-slate-600 font-medium">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
